<template>
  <div class="file-download-component">
    <!-- 下载进度模态框 -->
    <div v-if="showProgress" class="download-modal-overlay">
      <div class="download-modal-content" @click.stop>
        <div class="modal-header">
          <svg class="modal-icon" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14C0,17.31 2.69,20 6,20H19C21.76,20 24,17.76 24,15C24,12.36 21.95,10.22 19.35,10.04Z"/>
          </svg>
          <h3>文件下载</h3>
          <button class="close-btn" @click="cancelDownload">
            <i class="el-icon-close"></i>
          </button>
        </div>
        
        <div class="download-modal-body">
          <!-- 文件信息 -->
          <div class="file-info">
            <div class="file-icon">
              <i :class="getFileIconClass()"></i>
            </div>
            <div class="file-details">
              <div class="file-name-container">
                <div class="file-name" :title="currentFileName">{{ currentFileName }}</div>
              </div>
              <div class="file-size">{{ formatFileSize(currentFileSize) }}</div>
            </div>
          </div>
          
          <!-- 进度条 -->
          <div class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: downloadProgress + '%', backgroundColor: getProgressColor() }"></div>
            </div>
            <div class="progress-text">
              {{ Math.round(downloadProgress) }}%
            </div>
          </div>
          
          <!-- 下载详情 -->
          <div class="download-details">
            <div class="detail-item">
              <span class="detail-label">状态:</span>
              <span class="detail-value" :class="`status-${downloadStatusClass}`">{{ downloadStatus }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">速度:</span>
              <span class="detail-value">{{ downloadSpeed }}</span>
            </div>
          </div>
        </div>
        
        <div class="download-modal-footer">
          <button @click="cancelDownload" class="cancel-btn">取消下载</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { chunk_download_path, cancel_download_path, dir_compress_path } from '@/js/api_paths';
import { getName } from '@/js/tools';

export default {
  name: 'FileDownload',
  data() {
    return {
      showProgress: false,
      downloadProgress: 0,
      downloadedChunks: 0,
      totalChunks: 0,
      currentSessionId: null,
      isCancelled: false,
      currentFileName: '',
      currentFileSize: 0,
      startTime: null,
      lastChunkTime: null,
      downloadSpeed: '0 KB/s',
      downloadStatus: '准备中...',
      downloadStatusClass: 'pending',
      downloadSpeedHistory: [],
      chunkSize: 256 * 1024  // 0.25MB in bytes 
    }
  },
  methods: {
    init(fullPath){
        // 初始化下载状态
        this.isCancelled = false;
        this.showProgress = true;
        this.downloadProgress = 0;
        this.downloadedChunks = 0;
        this.currentFileName = getName(fullPath);
        this.startTime = Date.now();
        this.lastChunkTime = Date.now();
        this.downloadSpeed = '0 KB/s';
        this.downloadStatus = '准备中...';
        this.downloadStatusClass = 'pending';
        this.downloadSpeedHistory = [];
        this.chunkSize = 256 * 1024; // 0.25MB in bytes
    },

    async triggerDownload(downloadPath) {
      try {
        // 使用参数的路径
        let fullPath = downloadPath;
        if (!fullPath) {
          console.error('下载路径不能为空');
          return;
        }

        const token = this.$store.state.token;
        if (!token) {
          alert('请先登录');
          return;
        }

        this.init(fullPath); // 初始化下载状态

        // 判断是文件还是文件夹
        const isDirectory = fullPath.endsWith('/');
        
        // 如果是文件夹，先进行压缩
        if (isDirectory) {
          fullPath = await this.compressDir(fullPath, token);
        }

        // 生成下载会话ID
        this.currentSessionId = Date.now().toString() + Math.random().toString(36).substr(2);
        
        // 首先请求一次获取文件信息
        const initialResponse = await fetch(chunk_download_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sessionId: this.currentSessionId,
            relative_path: fullPath,
            offset: 0,
            chunkSize: this.chunkSize  // 现在以Bytes为单位
          })
        });

        const initialData = await initialResponse.json();
        
        if (initialData.status !== 'success') {
          throw new Error(initialData.msg || '获取文件信息失败');
        }

        // 从第一个响应中获取文件总大小
        this.currentFileSize = initialData.fileSize;
        this.totalChunks = initialData.totalChunks;
        
        // 重新获取第一块数据
        const firstChunkData = initialData.data;
        
        this.downloadStatus = '开始下载...';
        this.downloadStatusClass = 'downloading';

        const chunks = [];
        // 添加第一块数据
        const firstBinaryChunk = this.base64ToUint8Array(firstChunkData);
        chunks.push(firstBinaryChunk);
        
        // 更新第一个块的信息
        let downloadedSize = initialData.chunkSize;
        this.downloadedChunks = 1;
        
        // 在处理第一个块后立即更新进度和速度
        this.downloadProgress = (downloadedSize / this.currentFileSize) * 100;
        this.updateDownloadMetrics(initialData.chunkSize);

        let offset = initialData.chunkSize; // 下一个偏移量

        // 继续下载剩余的块
        while (!this.isCancelled && offset < this.currentFileSize) {
          const chunkResponse = await fetch(chunk_download_path, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              sessionId: this.currentSessionId,
              relative_path: fullPath,
              offset: offset,
              chunkSize: this.chunkSize  // 现在以Bytes为单位
            })
          });

          const chunkData = await chunkResponse.json();
          
          if (chunkData.status !== 'success') {
            throw new Error(chunkData.msg || '下载失败');
          }

          // 将块数据添加到数组
          const binaryChunk = this.base64ToUint8Array(chunkData.data);
          chunks.push(binaryChunk);
          
          downloadedSize += chunkData.chunkSize;
          offset += chunkData.chunkSize;
          this.downloadedChunks = Math.ceil(offset / this.chunkSize);

          // 更新进度
          this.downloadProgress = (downloadedSize / this.currentFileSize) * 100;

          // 更新下载速度
          this.updateDownloadMetrics(chunkData.chunkSize);

          // 检查是否是最后一个块
          if (chunkData.isLastChunk) {
            break;
          }
        }

        if (this.isCancelled) {
          // 取消下载
          await this.cancelDownloadSession();
          return;
        }

        // 合并所有块
        const totalChunks = chunks.reduce((acc, chunk) => acc + chunk.byteLength, 0);
        const fileBuffer = new Uint8Array(totalChunks);
        let position = 0;
        for (const chunk of chunks) {
          fileBuffer.set(new Uint8Array(chunk), position);
          position += chunk.byteLength;
        }

        // 创建 blob 并下载
        const blob = new Blob([fileBuffer]);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = this.currentFileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // 隐藏进度模态框
        this.downloadProgress = 100;
        this.downloadStatus = '下载完成';
        this.downloadStatusClass = 'success';
        this.downloadSpeed = '完成';
        
        setTimeout(() => {
          this.showProgress = false;
          this.currentSessionId = null;
        }, 1000);

      } catch (error) {
        console.error('下载文件时出错:', error);
        this.downloadStatus = '下载失败';
        this.downloadStatusClass = 'error';
        this.showProgress = false;
        this.currentSessionId = null;
        alert('下载失败: ' + error.message);
      }
    },

    // 压缩文件夹并准备下载
    async compressDir(directoryPath, token) {
      this.downloadStatus = '正在压缩文件夹...';
      this.downloadStatusClass = 'processing';
      
      try {
        const compressResponse = await fetch(dir_compress_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dir_path: directoryPath
          })
        });

        const compressData = await compressResponse.json();
        
        if (compressData.status !== 'success') {
          throw new Error(compressData.msg || '文件夹压缩失败');
        }
        
        // 更新文件名和状态
        this.currentFileName += ".zip"; // 如果下载的是文件夹,则为其加上.zip后缀
        this.downloadStatus = '压缩完成，开始下载...';
        this.downloadStatusClass = 'pending';
        
        // 返回压缩文件的路径
        return compressData.zip_path;
        
      } catch (compressError) {
        console.error('文件夹压缩失败:', compressError);
        throw new Error('文件夹压缩失败: ' + compressError.message);
      }
    },
    // 实时更新下载的速度信息
    updateDownloadMetrics(actualChunkSize) {
      const now = Date.now();
      const elapsed = (now - this.lastChunkTime) / 1000; // 秒
      
      // 使用实际的块大小（可能小于chunkSize，如最后一个块）
      const chunkSizeMB = actualChunkSize / (1024 * 1024); // 转换为MB
      const currentSpeed = chunkSizeMB / elapsed;
      
      this.downloadSpeedHistory.push(currentSpeed);
      if (this.downloadSpeedHistory.length > 5) {
        this.downloadSpeedHistory.shift();
      }
      
      // 计算平均速度
      const avgSpeed = this.downloadSpeedHistory.reduce((a, b) => a + b, 0) / this.downloadSpeedHistory.length;
      this.downloadSpeed = `${(avgSpeed).toFixed(2)} MB/s`;
      
      this.lastChunkTime = now;
    },

    // Base64字符串转Uint8Array
    base64ToUint8Array(base64) {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    },

    // 取消下载
    async cancelDownload() {
      this.isCancelled = true;
      await this.cancelDownloadSession();
      this.showProgress = false;
      this.currentSessionId = null;
    },

    // 取消下载会话
    async cancelDownloadSession() {
      try {
        const token = this.$store.state.token;
        if (token && this.currentSessionId) {
          await fetch(cancel_download_path, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              sessionId: this.currentSessionId
            })
          });
        }
      } catch (error) {
        console.error('取消下载会话失败:', error);
      }
    },

    getFileIconClass() {
      const ext = this.currentFileName.split('.').pop().toLowerCase();
      const iconMap = {
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture',
        'webp': 'el-icon-picture',
        'pdf': 'el-icon-document',
        'txt': 'el-icon-document',
        'md': 'el-icon-document',
        'markdown': 'el-icon-document',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-document',
        'xlsx': 'el-icon-document',
        'zip': 'el-icon-folder',
        'rar': 'el-icon-folder',
        'default': 'el-icon-document'
      };
      return iconMap[ext] || iconMap.default;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getProgressColor() {
      if (this.downloadStatusClass === 'success') return '#4CAF50';
      if (this.downloadStatusClass === 'error') return '#f44336';
      if (this.downloadStatusClass === 'downloading') return '#2196F3';
      return '#FF9800';
    }
  }
}
</script>

<style scoped>
.file-download-component {
  display: inline-block;
}

/* 模态框遮罩层 */
.download-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}

/* 模态框内容 */
.download-modal-content {
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  width: 500px;
  max-width: 95%;
  animation: modalFadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 模态框头部 */
.modal-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  background: linear-gradient(120deg, #8a2be2 0%, #9370db 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0; /* 防止头部被压缩 */
}

.modal-icon {
  color: rgba(255, 255, 255, 0.9);
  margin-right: 12px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
  flex: 1;
}

.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: auto;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* 模态框主体 */
.download-modal-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 重要：允许子元素正确计算高度 */
}

/* 文件信息 */
.file-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.file-icon {
  font-size: 24px;
  margin-right: 12px;
  color: #8a2be2;
  flex-shrink: 0; /* 防止图标被压缩 */
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 启用弹性收缩 */
}

.file-name-container {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  min-width: 0; /* 启用弹性收缩 */
}

.file-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  font-family: 'Monaco', 'Consolas', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0; /* 启用弹性收缩 */
}

/* 为文件名添加悬停显示完整名称 */
.file-name:hover {
  overflow: visible;
  position: relative;
  z-index: 1000;
}

.file-size {
  font-size: 12px;
  color: #666;
  flex-shrink: 0; /* 防止文件大小被压缩 */
}

/* 进度条容器 */
.progress-container {
  margin-bottom: 15px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background-color: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
  border: 1px solid #dcdfe6;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #8a2be2 0%, #6a0dad 100%);
  transition: width 0.3s ease, background-color 0.3s ease;
  border-radius: 6px;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
  background-size: 20px 20px;
  animation: progressAnimation 1s linear infinite;
  border-radius: 6px;
}

@keyframes progressAnimation {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 20px;
  }
}

.progress-text {
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 15px;
}

/* 下载详情 */
.download-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 15px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-label {
  color: #6c757d;
  font-size: 12px;
  font-weight: 500;
}

.detail-value {
  color: #606266;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detail-value.status-success {
  color: #67c23a;
}

.detail-value.status-error {
  color: #f56c6c;
}

.detail-value.status-pending {
  color: #e6a23c;
}

.detail-value.status-downloading {
  color: #409eff;
}

.detail-value.status-uploading {
  color: #409eff;
}

/* 模态框底部 */
.download-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  text-align: right;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0; /* 防止底部被压缩 */
}

.cancel-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.3);
}

.cancel-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.4);
  transform: translateY(-1px);
}

.cancel-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .download-modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .download-details {
    grid-template-columns: 1fr;
  }
  
  .modal-header h3 {
    font-size: 16px;
  }
  
  .download-modal-body {
    padding: 15px;
  }
  
  .file-name {
    font-size: 12px;
  }
  
  .file-size {
    font-size: 11px;
  }
}
</style>